import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { LoadingButton } from "@mui/lab";
import {
  Breadcrumbs,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import { instance } from "../../Const/ApiHeader";
import {
  ADD_SUBSCRIPTION,
  ALL_SUBSCRIPTION,
  SET_SUBSCRIPTION_VISIBILITY,
  UPDATE_SUBSCRIPTION,
} from "../../Const/ApiConst";

function buildGooglePlayProductId(planName, validity) {
  const normalizedPlan = (planName || "").toLowerCase().trim();

  if (normalizedPlan.includes("silver") || normalizedPlan.includes("standard")) {
    return "01235";
  }
  if (normalizedPlan.includes("gold")) {
    return "01234";
  }
  if (normalizedPlan.includes("platinum")) {
    return "1233";
  }

  const slugPlan = normalizedPlan
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const normalizedValidity = String(validity || "").replace(/[^0-9]/g, "");
  const planPart = slugPlan || "subscription";
  const validityPart = normalizedValidity || "plan";
  return `${planPart}_${validityPart}_days`;
}

function syncProductId(planName, validity, isEditing, currentProductId) {
  if (isEditing && currentProductId) {
    return currentProductId;
  }
  return buildGooglePlayProductId(planName, validity);
}

function PlanActions({ plan, busyId, onEdit, onToggleVisibility }) {
  const hidden = Boolean(plan.isHidden);
  const disabled = busyId === plan._id;

  return (
    <div className="subscription-plan-actions">
      <Tooltip title="Edit plan">
        <IconButton size="small" disabled={disabled} onClick={() => onEdit(plan)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={hidden ? "Show plan in app" : "Hide plan from app"}>
        <IconButton
          size="small"
          color={hidden ? "success" : "warning"}
          disabled={disabled}
          onClick={() => onToggleVisibility(plan)}
        >
          {hidden ? (
            <VisibilityOutlinedIcon fontSize="small" />
          ) : (
            <VisibilityOffOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
}

function Subscription() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [subsData, setSubsData] = useState([]);

  const [editingId, setEditingId] = useState("");
  const [subscriptionsName, setSubscriptionsName] = useState("");
  const [subscriptionAmount, setSubscriptionAmount] = useState("");
  const [planValidity, setPlanValidity] = useState("");
  const [googlePlayProductId, setGooglePlayProductId] = useState("");

  const isEditing = Boolean(editingId);

  const handleClose = () => {
    if (!saveLoading) {
      setShow(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setEditingId("");
    setSubscriptionsName("");
    setPlanValidity("");
    setSubscriptionAmount("");
    setGooglePlayProductId("");
  };

  const openAdd = () => {
    resetForm();
    setShow(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan._id);
    setSubscriptionsName(plan.plan_name || "");
    setPlanValidity(plan.planValidity || "");
    setSubscriptionAmount(plan.subscriptionAmount || "");
    setGooglePlayProductId(
      plan.googlePlayProductId ||
        buildGooglePlayProductId(plan.plan_name, plan.planValidity)
    );
    setShow(true);
  };

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await instance.get(ALL_SUBSCRIPTION);
      setSubsData(data?.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const saveSubscription = async () => {
    const productId =
      googlePlayProductId ||
      buildGooglePlayProductId(subscriptionsName, planValidity);

    const data = {
      plan_name: subscriptionsName,
      planValidity: planValidity,
      subscriptionAmount: subscriptionAmount,
      googlePlayProductId: productId,
    };

    setSaveLoading(true);
    try {
      if (isEditing) {
        await instance.post(UPDATE_SUBSCRIPTION, { id: editingId, ...data });
      } else {
        await instance.post(ADD_SUBSCRIPTION, data);
      }
      resetForm();
      await loadSubscriptions();
      setShow(false);
    } catch (err) {
      console.log(err);
    } finally {
      setSaveLoading(false);
    }
  };

  const togglePlanVisibility = async (plan) => {
    setBusyId(plan._id);
    try {
      await instance.post(SET_SUBSCRIPTION_VISIBILITY, {
        id: plan._id,
        isHidden: !plan.isHidden,
      });
      await loadSubscriptions();
    } catch (err) {
      console.log(err);
    } finally {
      setBusyId("");
    }
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const stats = useMemo(() => {
    const amounts = subsData
      .map((plan) => parseFloat(plan.subscriptionAmount))
      .filter((value) => !Number.isNaN(value));
    const validities = subsData
      .map((plan) => parseInt(plan.planValidity, 10))
      .filter((value) => !Number.isNaN(value));
    const visibleCount = subsData.filter((plan) => !plan.isHidden).length;

    const averageAmount =
      amounts.length > 0
        ? amounts.reduce((sum, value) => sum + value, 0) / amounts.length
        : 0;

    return {
      total: subsData.length,
      visibleCount,
      averageAmount,
      maxValidity: validities.length > 0 ? Math.max(...validities) : 0,
    };
  }, [subsData]);

  const query = search.trim().toLowerCase();
  const filteredPlans = useMemo(() => {
    return subsData.filter((plan) => {
      if (visibilityFilter === "visible" && plan.isHidden) return false;
      if (visibilityFilter === "hidden" && !plan.isHidden) return false;

      if (!query) return true;

      const haystack = [
        plan.plan_name,
        plan.planValidity,
        plan.subscriptionAmount,
        plan.googlePlayProductId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [subsData, query, visibilityFilter]);

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Subscription Plans</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <PaymentsOutlinedIcon fontSize="inherit" /> Billing catalogue
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Subscription Plans
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Manage plan pricing, validity, and Google Play product IDs.
          </Typography>
        </div>
        <Button variant="primary" onClick={openAdd}>
          <AddIcon className="me-1" /> Add Plan
        </Button>
      </section>

      <section className="vehicles-stats-grid">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.total}</Typography>
          <Typography className="vehicles-stat-label">Total plans</Typography>
        </div>
        <div className="vehicles-stat-card tone-green">
          <Typography className="vehicles-stat-value">
            {stats.visibleCount}
          </Typography>
          <Typography className="vehicles-stat-label">Visible in app</Typography>
        </div>
        <div className="vehicles-stat-card tone-blue">
          <Typography className="vehicles-stat-value">
            ₹ {stats.averageAmount.toFixed(0)}
          </Typography>
          <Typography className="vehicles-stat-label">Average price</Typography>
        </div>
        <div className="vehicles-stat-card tone-muted">
          <Typography className="vehicles-stat-value">
            {stats.maxValidity || "-"}
          </Typography>
          <Typography className="vehicles-stat-label">Longest validity (days)</Typography>
        </div>
      </section>

      <section className="section vehicles-toolbar">
        <div className="vehicles-toolbar-filters">
          <Form.Control
            className="admin-search"
            type="search"
            placeholder="Search by plan name, amount, or product ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Form.Select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
          >
            <option value="all">All plans</option>
            <option value="visible">Visible only</option>
            <option value="hidden">Hidden only</option>
          </Form.Select>
        </div>
        <Link to="/subscription-records">
          <Button variant="outline-primary">View subscription records</Button>
        </Link>
      </section>

      <section className="subscriptions-results">
        {loading ? (
          <div className="subscriptions-loading">
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading plans...</Typography>
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="section vehicles-empty">
            <ShoppingBagOutlinedIcon className="vehicles-empty-icon" />
            <Typography variant="h6">
              {subsData.length === 0 ? "No plans yet" : "No plans match your filters"}
            </Typography>
            <Typography color="text.secondary" className="mb-3">
              {subsData.length === 0
                ? "Create your first subscription plan for the mobile app."
                : "Try a different search term or visibility filter."}
            </Typography>
            {subsData.length === 0 ? (
              <Button variant="primary" onClick={openAdd}>
                <AddIcon className="me-1" /> Add Plan
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="subscriptions-grid">
            {filteredPlans.map((plan, index) => {
              const hidden = Boolean(plan.isHidden);
              const isBusy = busyId === plan._id;

              return (
                <article
                  key={plan._id || `${plan.plan_name}-${index}`}
                  className={`subscription-plan-card ${hidden ? "is-hidden" : ""} ${
                    isBusy ? "is-busy" : ""
                  }`}
                >
                  {isBusy ? (
                    <div className="subscription-plan-busy" aria-label="Updating plan">
                      <CircularProgress size={24} />
                    </div>
                  ) : null}

                  <div className="subscription-plan-header">
                    <Typography className="subscription-plan-name">
                      {plan.plan_name}
                    </Typography>
                    <div className="subscription-plan-header-actions">
                      <Chip
                        size="small"
                        icon={<CalendarMonthOutlinedIcon />}
                        label={`${plan.planValidity} days`}
                      />
                      <PlanActions
                        plan={plan}
                        busyId={busyId}
                        onEdit={openEdit}
                        onToggleVisibility={togglePlanVisibility}
                      />
                    </div>
                  </div>

                  {hidden ? (
                    <Chip
                      size="small"
                      color="default"
                      label="Hidden from app"
                      className="subscription-plan-hidden-badge"
                    />
                  ) : null}

                  <div className="subscription-plan-price">
                    <Typography className="subscription-plan-amount">
                      ₹ {plan.subscriptionAmount}
                    </Typography>
                    <Typography className="subscription-plan-price-label">
                      Plan price
                    </Typography>
                  </div>

                  <div className="subscription-plan-meta">
                    <div>
                      <Typography className="subscription-plan-meta-label">
                        Google Play product ID
                      </Typography>
                      <Typography className="subscription-plan-meta-value">
                        {plan.googlePlayProductId || "-"}
                      </Typography>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit subscription plan" : "Add subscription plan"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label>Plan name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Premium Annual"
              value={subscriptionsName}
              onChange={(e) => {
                const value = e.target.value;
                setSubscriptionsName(value);
                setGooglePlayProductId(
                  syncProductId(value, planValidity, isEditing, googlePlayProductId)
                );
              }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Validity (days)</Form.Label>
            <Form.Control
              type="number"
              min="1"
              placeholder="e.g. 30"
              value={planValidity}
              onChange={(e) => {
                const value = e.target.value;
                setPlanValidity(value);
                setGooglePlayProductId(
                  syncProductId(subscriptionsName, value, isEditing, googlePlayProductId)
                );
              }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Amount (₹)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="e.g. 499"
              value={subscriptionAmount}
              onChange={(e) => setSubscriptionAmount(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Google Play product ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Must match Google Play Console exactly (e.g. 01235)"
              value={googlePlayProductId}
              onChange={(e) => setGooglePlayProductId(e.target.value)}
            />
            <Form.Text className="text-muted">
              Standard/Silver → 01235, Gold → 01234, Platinum → 1233
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saveLoading}>
            Cancel
          </Button>
          <LoadingButton
            variant="primary"
            loading={saveLoading}
            onClick={saveSubscription}
          >
            {isEditing ? "Update plan" : "Save plan"}
          </LoadingButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Subscription;
